import React from "react";
import {
  Box,
  Typography,
  Container,
  Grid,
  Paper,
  Divider,
  Chip,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";

const PRIMARY = "#CC0000";

const itFeatures = [
  "独自の開発アプローチと情報設計により、拡張性と操作性に優れたシステム構成を提案",
  "長期的なパートナーとして、開発・保守・運用を一貫して行い課題解決を確実にする",
  "運用ガイドラインと技術共有により、システムの標準化とお客様の内製化を可能にする",
];

const snsRecruitFeatures = [
  "採用専用のブランディング戦略を構築",
  "候補者の心に響くコンテンツや運用メソッドを提供",
  "ノウハウ提供や内製化支援",
];

const snsOperationFeatures = [
  "顧客の心を動かすエンゲージメントを設計",
  "「認知」から「流入・問い合わせ」へと最大化",
  "潜在顧客を確実なビジネスチャンスへと転換",
];

const caseStudies = [
  {
    client: "ココナスポーツクラブ",
    industry: "サービス業・子供のスポーツ教室",
    scale: "顧客 約1,000人 / 10店舗 / 従業員 約30名",
    services: ["顧客決済管理システム【開発・保守・運用】", "シフト管理システム【開発・保守・運用】", "SNS運用代行"],
    challenge:
      "決済と顧客情報管理の分断が最大の課題。既存システムでは決済が銀行振込のみで、手作業による入金照合で事務負荷とヒューマンエラーが常態化。データが分断されていたため、正確な売上分析やデータ活用が不可能でした。",
    results: [
      { label: "照合作業時間", value: "約80%削減", icon: "⏱" },
      { label: "Web経由申込率", value: "約2倍アップ", icon: "📈" },
      { label: "業務効率", value: "継続的に改善", icon: "⚙️" },
    ],
  },
  {
    client: "土日だけ体操教室",
    industry: "サービス業・子供のスポーツ教室（フランチャイズ）",
    scale: "顧客 約200人 / 2店舗 / 従業員 約5名",
    services: ["顧客決済管理システム【導入・保守・運用】", "シフト管理システム【導入・保守・運用】", "SNS運用代行"],
    challenge:
      "事業拡大（フランチャイズ）に伴い、手作業に依存していた決済・顧客管理・シフト管理の負荷が限界に達していました。多店舗展開を支える業務効率化とデータ一元化、そして安定的な集客を実現する仕組みを一括で構築するため依頼されました。",
    results: [
      { label: "照合作業時間", value: "大幅削減", icon: "⏱" },
      { label: "Web経由申込率", value: "約4倍アップ", icon: "📈" },
      { label: "業務効率", value: "継続的に改善", icon: "⚙️" },
    ],
  },
];

const ServicePage = () => {
  return (
    <Box>
      {/* Page Hero */}
      <Box sx={{ bgcolor: PRIMARY, color: "white", py: { xs: 8, md: 12 } }}>
        <Container maxWidth="lg">
          <Typography
            sx={{ fontSize: "0.85rem", fontWeight: 600, letterSpacing: 3, mb: 2, opacity: 0.85 }}
          >
            Service
          </Typography>
          <Typography
            variant="h2"
            sx={{ fontWeight: 900, fontSize: { xs: "2rem", md: "3rem" } }}
          >
            サービス内容
          </Typography>
          <Typography sx={{ mt: 2, opacity: 0.85, maxWidth: 560 }}>
            「IT」と「SNS」を軸に、お客様の業務の最適化と企業価値の最大化を両面から支援します。
          </Typography>
        </Container>
      </Box>

      {/* IT事業 */}
      <Box sx={{ py: { xs: 8, md: 12 } }}>
        <Container maxWidth="lg">
          <Box sx={{ mb: 2, display: "flex", alignItems: "center", gap: 2 }}>
            <Box sx={{ width: 4, height: 32, bgcolor: PRIMARY }} />
            <Typography
              sx={{ fontSize: "0.85rem", fontWeight: 700, letterSpacing: 3, color: PRIMARY }}
            >
              IT事業
            </Typography>
          </Box>
          <Typography
            variant="h3"
            sx={{ fontWeight: 900, mb: 2, fontSize: { xs: "1.8rem", md: "2.5rem" } }}
          >
            システム【開発・保守・運用】
          </Typography>
          <Typography sx={{ color: "#666", mb: 2, fontWeight: 600 }}>
            ビジネス課題を的確に分析　企業の成長に直結するシステムを設計・構築
          </Typography>
          <Typography sx={{ color: "#888", mb: 1, fontSize: "0.85rem", fontStyle: "italic" }}>
            「 作るで終わらない、継続的な安定稼働と進化を前提としたシステム構築」という価値を提供します
          </Typography>

          <Divider sx={{ my: 5 }} />

          <Grid container spacing={3}>
            {itFeatures.map((feature, i) => (
              <Grid item xs={12} md={4} key={i}>
                <Box
                  sx={{
                    display: "flex",
                    gap: 2,
                    p: 3,
                    border: "1px solid #e0e0e0",
                    height: "100%",
                  }}
                >
                  <CheckCircleOutlineIcon sx={{ color: PRIMARY, mt: 0.3, flexShrink: 0 }} />
                  <Typography sx={{ fontSize: "0.9rem", lineHeight: 1.8 }}>{feature}</Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* SNS事業 */}
      <Box sx={{ py: { xs: 8, md: 12 }, bgcolor: "#f8f8f8" }}>
        <Container maxWidth="lg">
          <Box sx={{ mb: 2, display: "flex", alignItems: "center", gap: 2 }}>
            <Box sx={{ width: 4, height: 32, bgcolor: PRIMARY }} />
            <Typography
              sx={{ fontSize: "0.85rem", fontWeight: 700, letterSpacing: 3, color: PRIMARY }}
            >
              SNS事業
            </Typography>
          </Box>
          <Typography
            variant="h3"
            sx={{ fontWeight: 900, mb: 1, fontSize: { xs: "1.8rem", md: "2.5rem" } }}
          >
            SNS採用 / SNS運用
          </Typography>
          <Typography sx={{ color: "#888", mb: 1, fontSize: "0.85rem", fontStyle: "italic" }}>
            「企業の魅力を最大限に引き出し、集客・採用に直結させる」という価値を提供します
          </Typography>

          <Divider sx={{ my: 5 }} />

          <Grid container spacing={6}>
            {/* SNS採用 */}
            <Grid item xs={12} md={6}>
              <Paper elevation={0} sx={{ p: 4, border: `2px solid ${PRIMARY}`, borderRadius: 0 }}>
                <Typography
                  variant="h5"
                  sx={{ fontWeight: 900, mb: 1, color: PRIMARY }}
                >
                  SNS採用
                </Typography>
                <Typography sx={{ color: "#666", fontSize: "0.85rem", mb: 3 }}>
                  採用力に直結させるためのSNSリクルーティングを提供
                </Typography>
                <Chip label="toC" size="small" sx={{ bgcolor: "#1a1a1a", color: "white", mr: 1, mb: 3 }} />
                <Chip label="toB" size="small" sx={{ bgcolor: "#1a1a1a", color: "white", mb: 3 }} />
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  {snsRecruitFeatures.map((f, i) => (
                    <Box key={i} sx={{ display: "flex", gap: 2 }}>
                      <CheckCircleOutlineIcon sx={{ color: PRIMARY, mt: 0.3, flexShrink: 0 }} />
                      <Typography sx={{ fontSize: "0.9rem", lineHeight: 1.8 }}>{f}</Typography>
                    </Box>
                  ))}
                </Box>
              </Paper>
            </Grid>

            {/* SNS運用 */}
            <Grid item xs={12} md={6}>
              <Paper elevation={0} sx={{ p: 4, border: `2px solid ${PRIMARY}`, borderRadius: 0 }}>
                <Typography
                  variant="h5"
                  sx={{ fontWeight: 900, mb: 1, color: PRIMARY }}
                >
                  SNS運用
                </Typography>
                <Typography sx={{ color: "#666", fontSize: "0.85rem", mb: 3 }}>
                  ブランド価値を熱量高く伝え、流入と問い合わせを最大化
                </Typography>
                <Chip label="toC" size="small" sx={{ bgcolor: "#1a1a1a", color: "white", mr: 1, mb: 3 }} />
                <Chip label="toB" size="small" sx={{ bgcolor: "#1a1a1a", color: "white", mb: 3 }} />
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  {snsOperationFeatures.map((f, i) => (
                    <Box key={i} sx={{ display: "flex", gap: 2 }}>
                      <CheckCircleOutlineIcon sx={{ color: PRIMARY, mt: 0.3, flexShrink: 0 }} />
                      <Typography sx={{ fontSize: "0.9rem", lineHeight: 1.8 }}>{f}</Typography>
                    </Box>
                  ))}
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* 導入事例 */}
      <Box sx={{ py: { xs: 8, md: 12 } }}>
        <Container maxWidth="lg">
          <Typography
            sx={{
              fontSize: "0.85rem",
              fontWeight: 600,
              letterSpacing: 3,
              color: PRIMARY,
              mb: 1,
            }}
          >
            Case Studies
          </Typography>
          <Typography
            variant="h3"
            sx={{ fontWeight: 900, mb: 8, fontSize: { xs: "1.8rem", md: "2.5rem" } }}
          >
            導入事例
          </Typography>

          <Box sx={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {caseStudies.map((cs, i) => (
              <Paper
                key={i}
                elevation={0}
                sx={{ border: "1px solid #e0e0e0", overflow: "hidden" }}
              >
                {/* ヘッダー */}
                <Box sx={{ bgcolor: PRIMARY, px: 4, py: 3 }}>
                  <Typography variant="h5" sx={{ color: "white", fontWeight: 900 }}>
                    {cs.client}
                  </Typography>
                  <Typography sx={{ color: "rgba(255,255,255,0.8)", fontSize: "0.85rem" }}>
                    {cs.industry}
                  </Typography>
                </Box>

                <Grid container>
                  {/* 左：会社概要・課題 */}
                  <Grid item xs={12} md={7} sx={{ p: 4, borderRight: { md: "1px solid #e0e0e0" } }}>
                    <Typography
                      sx={{ fontWeight: 700, fontSize: "0.8rem", color: "#888", letterSpacing: 2, mb: 1 }}
                    >
                      会社規模
                    </Typography>
                    <Typography sx={{ mb: 3, fontSize: "0.9rem" }}>{cs.scale}</Typography>

                    <Typography
                      sx={{ fontWeight: 700, fontSize: "0.8rem", color: "#888", letterSpacing: 2, mb: 1 }}
                    >
                      導入サービス
                    </Typography>
                    <Box sx={{ mb: 3 }}>
                      {cs.services.map((s, si) => (
                        <Box key={si} sx={{ display: "flex", gap: 1, mb: 0.5 }}>
                          <Typography sx={{ color: PRIMARY, fontWeight: 700 }}>•</Typography>
                          <Typography sx={{ fontSize: "0.9rem" }}>{s}</Typography>
                        </Box>
                      ))}
                    </Box>

                    <Typography
                      sx={{ fontWeight: 700, fontSize: "0.8rem", color: "#888", letterSpacing: 2, mb: 1 }}
                    >
                      導入前の課題
                    </Typography>
                    <Typography sx={{ fontSize: "0.9rem", lineHeight: 1.9, color: "#555" }}>
                      {cs.challenge}
                    </Typography>
                  </Grid>

                  {/* 右：実績・効果 */}
                  <Grid item xs={12} md={5} sx={{ p: 4, bgcolor: "#fafafa" }}>
                    <Typography
                      sx={{ fontWeight: 700, fontSize: "0.8rem", color: "#888", letterSpacing: 2, mb: 3 }}
                    >
                      実績・効果
                    </Typography>
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
                      {cs.results.map((r, ri) => (
                        <Box
                          key={ri}
                          sx={{
                            p: 3,
                            bgcolor: "white",
                            border: `1px solid ${PRIMARY}`,
                            textAlign: "center",
                          }}
                        >
                          <Typography sx={{ fontSize: "1.5rem", mb: 1 }}>{r.icon}</Typography>
                          <Typography sx={{ fontSize: "0.8rem", color: "#888", mb: 0.5 }}>
                            {r.label}
                          </Typography>
                          <Typography
                            sx={{ fontSize: "1.3rem", fontWeight: 900, color: PRIMARY }}
                          >
                            {r.value}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  </Grid>
                </Grid>
              </Paper>
            ))}
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default ServicePage;
